import { useLoaderData, useLocation, useNavigate } from 'react-router-dom';
import React, { useCallback, useMemo } from 'react';
import { Button, ButtonAppearance, Heading } from '@atlassian/learning-components';
import { token } from '@atlaskit/tokens';
import { css } from '@compiled/react';
import { media } from '@atlaskit/primitives/responsive';

import { LessonLoaderProps } from 'src/pages/LessonPage';
import { getSlug } from 'src/utils/slug';
import { getLearningPathsHubPart } from 'src/utils/pathUtils';
import { ScrollBehaviorTypes, setScrollBehaviour } from 'src/utils/lessonScrollBehavior';
import { getLessonUrl, withPrefix } from 'src/utils/getUrl';
import { QuickFeedbackWrapper } from 'src/pages/LessonPage/children/QuickFeedbackWrapper';
import { useProgressState } from 'src/store/progress/progressStore';
import { getNextCourse, findNextLessonSlug } from 'src/pages/LessonPage/children/utils/nextCourseUtils';
import { LearningProgressStatus } from 'src/services/http/progress';

interface NextCourseProps {
  nextCourseBackground: string;
}

export const NextCourse: React.FC<NextCourseProps> = ({ nextCourseBackground }) => {
  const { learningPath, course, lesson } = useLoaderData() as LessonLoaderProps;
  const navigate = useNavigate();
  const location = useLocation();
  const [{ progressForCourses, progressForLessons }] = useProgressState();

  const learningPathSlug = getSlug(learningPath);

  const nextCourse = getNextCourse(learningPath, course, progressForCourses);
  const nextLessonSlug = findNextLessonSlug(nextCourse, progressForLessons);

  const onNextCourseClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();

    const allLearningPathsHubPart = getLearningPathsHubPart(location.pathname);

    // to prevent lesson auto completion
    setScrollBehaviour(ScrollBehaviorTypes.UNSET);

    const nextCourseSlug = getSlug(nextCourse);

    if (nextCourseSlug && nextLessonSlug) {
      const lessonUrl = getLessonUrl({
        lessonSlug: nextLessonSlug,
        courseSlug: nextCourseSlug,
        pathSlug: learningPathSlug,
      });

      const prefixedUrl = withPrefix(lessonUrl, allLearningPathsHubPart);

      navigate(prefixedUrl, { state: { isNextCourseNavigation: true } });
    }
  };

  const getNextCourseUrl = useCallback((): string => {
    if (!nextCourse || !nextLessonSlug) {
      return '';
    }

    const nextCourseSlug = getSlug(nextCourse);
    const baseUrl = getLessonUrl({
      lessonSlug: nextLessonSlug,
      courseSlug: nextCourseSlug,
      pathSlug: learningPathSlug,
    });

    const hubPart = getLearningPathsHubPart(location.pathname);
    const url = hubPart ? `${hubPart}${baseUrl}` : baseUrl;

    return withPrefix(url);
  }, [nextCourse, learningPathSlug, location.pathname, nextLessonSlug]);

  // if all lessons are completed except for current one - show the next course section
  const shouldRenderNextCourseSection = useMemo(() => {
    if (!course || !lesson || !nextCourse || !progressForLessons) {
      return false;
    }

    const allLessonsExceptCurrent = course.lessonsCollection.items.filter((l) => l.sys.id !== lesson.sys.id);
    const areAllPreviousLessonsCompleted = allLessonsExceptCurrent.every(
      (l) =>
        progressForLessons.lessons.find((p) => p.lessonId === l.sys.id)?.status === LearningProgressStatus.COMPLETED,
    );

    return areAllPreviousLessonsCompleted;
  }, [course, lesson, nextCourse, progressForLessons]);

  if (!shouldRenderNextCourseSection) {
    return null;
  }

  return (
    <section css={sectionContainerStyles} id="next-course-section" style={{ backgroundColor: nextCourseBackground }}>
      <QuickFeedbackWrapper isNextItemAvailable={shouldRenderNextCourseSection} />
      <div css={nextSectionContainer} data-testid="next-course-section">
        <p css={nextCourseHeading}>next course</p>
        <div css={css({ marginBottom: token('space.200') })}>
          <Heading level={3}>{nextCourse?.title}</Heading>
        </div>
        <ul css={courseObjectivesStyles}>
          {nextCourse &&
            nextCourse.lessonsCollection.items.map((lesson) => (
              <li css={css({ marginLeft: token('space.300') })} key={lesson.sys.id}>
                {lesson.title}
              </li>
            ))}
        </ul>

        <div css={nextCourseButtonStyles}>
          <Button
            label="Go to next course"
            appearance={ButtonAppearance.PRIMARY}
            onClick={onNextCourseClick}
            target="_blank"
            href={getNextCourseUrl()}
          />
        </div>
      </div>
    </section>
  );
};

const sectionContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: token('space.1000'),
  paddingBottom: token('space.1000'),
  paddingLeft: token('space.250'),
  paddingRight: token('space.250'),
});

const nextSectionContainer = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  maxWidth: '608px',
  width: '100%',
});

const nextCourseHeading = css({
  fontFamily: 'Charlie Text, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '18px',
  color: token('color.text.subtle'),
  marginBottom: token('space.200'),
  textTransform: 'uppercase',
});

const courseObjectivesStyles = css({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  color: token('color.text'),
  listStyleType: 'disc',
  marginTop: 0,
  marginBottom: token('space.500'),
  paddingLeft: 0,
});

const nextCourseButtonStyles = css({
  marginLeft: 'auto',

  [media.below.sm]: {
    marginRight: 'auto',
    marginLeft: 0,
  },
});
